import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { Observable } from '../utils/Observable';
import { isObject, isString } from '../utils/typeGuards';
import { allowedOrigins } from '../constants';
const isMessageData = data => {
  var _data$type;
  return isObject(data) && isString(data.type) && isObject(data.message) && (data === null || data === void 0 || (_data$type = data.type) === null || _data$type === void 0 ? void 0 : _data$type.includes(':'));
};
const embeddedAcademyKey = 'embeddedAcademy';
const formatMessage = (messengerKey, messageKey, message) => {
  const messageToSend = {
    type: `${embeddedAcademyKey}:${messengerKey}:${messageKey}`,
    message
  };
  return messageToSend;
};
const parseMessage = (data, key) => {
  var _data$type2;
  if (isMessageData(data) && data !== null && data !== void 0 && (_data$type2 = data.type) !== null && _data$type2 !== void 0 && _data$type2.includes(`${embeddedAcademyKey}:${key}`)) {
    const parts = data.type.split(':');
    const event = parts[parts.length - 1];
    if (event) {
      return [event, data.message];
    }
  }
};
const handshakeMessageKey = 'handshake';
const readyMessageKey = 'ready';
const dispatchHandlers = (messageData, set, messageHandlers) => {
  if (messageHandlers && messageData) {
    const [handlerKey, message] = messageData;
    const handler = messageHandlers[handlerKey];
    if (handler) {
      handler(message, set);
    }
  }
};
class OutsideFrameMessenger extends Observable {
  constructor(iFrame, key, messageHandlers = {}) {
    super();
    this.iFrame = null;
    this.ownMessageHandlers = {
      [readyMessageKey]: () => {
        this.sendMessage(handshakeMessageKey, {
          domain: window.location.origin
        });
      }
    };
    this.setupMessageHandlers = () => {
      const handleMessage = e => {
        var _this$iFrame;
        if (e.source !== ((_this$iFrame = this.iFrame) === null || _this$iFrame === void 0 ? void 0 : _this$iFrame.contentWindow)) return;
        if (!allowedOrigins.includes(e.origin)) return;
        const msgData = parseMessage(e.data, this.key);
        if (msgData) {
          dispatchHandlers(msgData, this.setState, this.ownMessageHandlers);
          dispatchHandlers(msgData, this.setState, this.messageHandlers);
        }
      };
      window.addEventListener('message', handleMessage);
      return () => window.removeEventListener('message', handleMessage);
    };
    this.sendMessage = (messageKey, message) => {
      const messageToSend = formatMessage(this.key, messageKey, message);
      if (this.iFrame && this.iFrame.contentWindow) {
        this.iFrame.contentWindow.postMessage(messageToSend, new URL(this.iFrame.src).origin);
      }
    };
    this.key = key;
    this.iFrame = iFrame;
    this.messageHandlers = messageHandlers;
    this.setupMessageHandlers();
  }
}
class InsideFrameMessenger extends Observable {
  constructor(key) {
    super();
    this.parent = window.parent;
    this.ownMessageHandlers = {
      [handshakeMessageKey]: message => {
        if (!allowedOrigins.includes(message.domain)) {
          console.error('Invalid handshake message domain', message.domain);
          return;
        }
        this.outsideFrameDomain = message.domain;
      }
    };
    this.setupMessageHandlers = (messageHandlers = {}, allowExternal) => {
      this.messageHandlers = messageHandlers;
      const handleMessage = e => {
        if (e.source !== window.parent) return;
        if (!allowExternal && !allowedOrigins.includes(e.origin)) return;
        const msgData = parseMessage(e.data, this.key);
        dispatchHandlers(msgData, this.setState, this.ownMessageHandlers);
        dispatchHandlers(msgData, this.setState, this.messageHandlers);
      };
      window.addEventListener('message', handleMessage);
      this.sendMessage(readyMessageKey, {
        ready: true
      });
      return () => window.removeEventListener('message', handleMessage);
    };
    this.sendMessage = (messageKey, message, allowExternal) => {
      const messageToSend = formatMessage(this.key, messageKey, message);
      if (messageKey === readyMessageKey) {
        allowedOrigins.forEach(origin => {
          window.parent.postMessage(messageToSend, origin);
        });
      } else if (this.outsideFrameDomain) {
        window.parent.postMessage(messageToSend, this.outsideFrameDomain);
      } else if (allowExternal) {
        window.parent.postMessage(messageToSend, '*');
      }
    };
    this.key = key;
  }
}
/**
 * For each messenger type, we keep a registry of iframes and their corresponding messengers.
 * This allows us to have a single messenger for each iframe. Hooks that use the messenger store
 * will always be reading the same data from the same source.
 */
const outsideMessengerRegistry = {
  syncHeight: new Map(),
  dynamic: new Map(),
  static: new Map()
};
const insideMessengerRegistry = {
  syncHeight: new InsideFrameMessenger('syncHeight'),
  dynamic: new InsideFrameMessenger('dynamic'),
  static: new InsideFrameMessenger('static')
};
const noop = () => {};
const getNullSyncStore = () => [() => {
  return noop;
}, noop];
export const useOutsideFrameMessenger = ({
  key,
  messageHandlers
}) => {
  const [messenger, setMessenger] = useState();
  const [isReady, setIsReady] = useState(false);
  const registry = outsideMessengerRegistry[key];
  const [subscribe, getState] = messenger ? [messenger.subscribe, messenger.getState] : getNullSyncStore();
  const state = useSyncExternalStore(subscribe, getState);
  const registerIFrame = useCallback(iFrame => {
    if (!iFrame) return;
    if (!registry.has(iFrame)) {
      const handlers = Object.assign({}, messageHandlers, {
        [readyMessageKey]: () => {
          setIsReady(true);
        }
      });
      const newMessenger = new OutsideFrameMessenger(iFrame, key, handlers);
      registry.set(iFrame, newMessenger);
      setMessenger(newMessenger);
    } else {
      setMessenger(registry.get(iFrame));
    }
  }, [key, messageHandlers, registry]);
  return {
    isReady,
    state,
    registerIFrame,
    sendMessage: messenger === null || messenger === void 0 ? void 0 : messenger.sendMessage
  };
};
export const useInsideFrameMessenger = ({
  key,
  messageHandlers,
  allowExternal
}) => {
  const messenger = insideMessengerRegistry[key];
  useEffect(() => {
    const unsubscribe = messenger.setupMessageHandlers(messageHandlers, allowExternal);
    return () => {
      unsubscribe();
    };
  }, [allowExternal, key, messageHandlers, messenger]);
  const state = useSyncExternalStore(messenger.subscribe, messenger.getState);
  return {
    state,
    sendMessage: messenger.sendMessage
  };
};
export const simpleStateSetHandler = (message, setState) => {
  setState(message);
};